export const CONSTANTS = {
  // ---------------------------------------------------------------------------
  // GAME DEFAULTS & INITIAL STATE
  // ---------------------------------------------------------------------------
  STARTING_COINS: 500,
  STARTING_INVENTORY: {
    water: 10,
    beans: 10,
    milk: 10,
    sugar: 10,
    ice: 10,
    grinder: 1,
    espresso_machine: 1,
    steamer: 1
  },
  DEFAULT_SETTINGS: {
    musicVolume: 0.5,
    sfxVolume: 0.8,
  },
  DEFAULT_SESSION_MODE: {
    type: 'endless',
    startTime: 0, // Will be set at runtime
    score: 0,
    served: 0,
    tips: 0,
    combosDiscovered: 0,
  },

  // ---------------------------------------------------------------------------
  // TUTORIAL AUTO-PROVISIONING SYSTEM
  // ---------------------------------------------------------------------------
  TUTORIAL_AUTO_PROVISION: {
    ENABLED: true,
    QUANTITY_PER_INGREDIENT: 1,
    SHOW_NOTIFICATION: true,
    NOTIFICATION_MESSAGE: "Added {count} missing ingredients for tutorial",
    NOTIFICATION_DURATION: 3000,
  },

  // ---------------------------------------------------------------------------
  // MOBILE UI CONSTANTS - Optimized for compact mobile experience
  // ---------------------------------------------------------------------------
  MOBILE_UI: {
    // Screen breakpoints
    MOBILE_BREAKPOINT: 640, // Tailwind sm breakpoint
    SMALL_MOBILE_BREAKPOINT: 480, // Very small mobile breakpoint
    LANDSCAPE_MOBILE_HEIGHT: 500, // Height threshold for landscape mobile detection
    
    // Header sizing - more compact
    HEADER_HEIGHT_MOBILE: 32,
    HEADER_PADDING_MOBILE: 4,
    HEADER_PADDING_SMALL_MOBILE: 2,
    HEADER_FONT_SIZE_MOBILE: 12,
    HEADER_FONT_SIZE_SMALL_MOBILE: 10,
    
    // Inventory item sizing - optimized for information density
    INVENTORY_ITEM_SIZE_MOBILE: 56, // Reduced from default for more items per row
    INVENTORY_ITEM_SIZE_SMALL_MOBILE: 48,
    INVENTORY_ITEM_ICON_SIZE_MOBILE: 32,
    INVENTORY_ITEM_ICON_SIZE_SMALL_MOBILE: 28,
    INVENTORY_ITEM_TEXT_SIZE_MOBILE: 10,
    INVENTORY_ITEM_TEXT_SIZE_SMALL_MOBILE: 8,
    INVENTORY_GRID_COLS_MOBILE: 4, // More columns for compact layout
    INVENTORY_GRID_COLS_SMALL_MOBILE: 3,
    INVENTORY_GRID_GAP_MOBILE: 6,
    INVENTORY_GRID_GAP_SMALL_MOBILE: 4,
    
    // Customer card sizing - compact but readable
    CUSTOMER_CARD_PADDING_MOBILE: 8,
    CUSTOMER_CARD_PADDING_SMALL_MOBILE: 6,
    CUSTOMER_CARD_ICON_SIZE_MOBILE: 32,
    CUSTOMER_CARD_ICON_SIZE_SMALL_MOBILE: 28,
    CUSTOMER_CARD_TITLE_SIZE_MOBILE: 12,
    CUSTOMER_CARD_TITLE_SIZE_SMALL_MOBILE: 11,
    CUSTOMER_CARD_SUBTITLE_SIZE_MOBILE: 10,
    CUSTOMER_CARD_SUBTITLE_SIZE_SMALL_MOBILE: 9,
    CUSTOMER_CARD_SERVE_BUTTON_HEIGHT_MOBILE: 32,
    CUSTOMER_CARD_SERVE_BUTTON_HEIGHT_SMALL_MOBILE: 28,
    
    // Panel sizing
    PANEL_WIDTH_MOBILE: 280, // Fixed width for consistency
    PANEL_WIDTH_SMALL_MOBILE: 240,
    
    // Tutorial modal sizing
    TUTORIAL_MODAL_MAX_WIDTH_MOBILE: 280,
    TUTORIAL_MODAL_MAX_WIDTH_SMALL_MOBILE: 240,
    TUTORIAL_MODAL_PADDING_MOBILE: 12,
    TUTORIAL_MODAL_PADDING_SMALL_MOBILE: 10,
    
    // General compact sizing
    COMPACT_BUTTON_HEIGHT: 32,
    COMPACT_ICON_SIZE: 16,
    COMPACT_TEXT_SIZE: 12,
    COMPACT_TEXT_SIZE_SMALL: 10,
  },

  // ---------------------------------------------------------------------------
  // BREW STATION GRID-BASED SIZING - Dynamic sizing based on grid
  // ---------------------------------------------------------------------------
  BREW_STATION_GRID: {
    // Mobile grid dimensions in tiles
    WIDTH_TILES: 4, // How many grid tiles wide the brew station should be on mobile
    HEIGHT_TILES: 5, // How many grid tiles tall the brew station should be on mobile
    
    // Desktop grid dimensions in tiles - larger for better desktop experience
    DESKTOP_WIDTH_TILES: 6, // How many grid tiles wide the brew station should be on desktop
    DESKTOP_HEIGHT_TILES: 7, // How many grid tiles tall the brew station should be on desktop
    
    // Proportional sizing ratios (relative to container width)
    PADDING_RATIO: 0.05, // Container padding as ratio of width
    GAP_RATIO: 0.02, // Element gap as ratio of width
    TITLE_SIZE_RATIO: 0.06, // Title font size as ratio of width
    TEXT_SIZE_RATIO: 0.045, // General text size as ratio of width
    SLOT_HEIGHT_RATIO: 0.25, // Slot height as ratio of width
    BUTTON_HEIGHT_RATIO: 0.2, // Button height as ratio of width
    BUTTON_SIZE_RATIO: 0.15, // Quantity button size as ratio of width
    ICON_SIZE_RATIO: 0.08, // Icon size as ratio of width
    
    // Minimum sizes to ensure usability - Desktop
    MIN_PADDING: 4,
    MIN_GAP: 2,
    MIN_TITLE_SIZE: 8,
    MIN_TEXT_SIZE: 7,
    MIN_SLOT_HEIGHT: 24,
    MIN_BUTTON_HEIGHT: 20,
    MIN_BUTTON_SIZE: 16,
    MIN_ICON_SIZE: 10,
    
    // Minimum sizes for mobile - More compact (REDUCED)
    MIN_MOBILE_BUTTON_HEIGHT: 24,
    MIN_MOBILE_BUTTON_SIZE: 10, // Reduced from 12
  },

  // ---------------------------------------------------------------------------
  // TOUCH INTERACTION CONSTANTS - Optimized for mobile
  // ---------------------------------------------------------------------------
  TOUCH_INTERACTION: {
    MIN_TOUCH_TARGET_SIZE: 25, // Reduced for compact mobile UI, standard is 44px
    DRAG_THRESHOLD: 10,
    TOUCH_FEEDBACK_DURATION: 150,
    HAPTIC_FEEDBACK_ENABLED: true,
    LONG_PRESS_DURATION: 500,
    DOUBLE_TAP_THRESHOLD: 300,
    SCROLL_LOCK_THRESHOLD: 5,
    TOUCH_RIPPLE_DURATION: 600,
    GESTURE_SENSITIVITY: {
      LATTE_ART_MIN_DISTANCE: 5,
      LATTE_ART_SMOOTHING: 0.3,
    },
  },

  // ---------------------------------------------------------------------------
  // GRID SYSTEM - Optimized for mobile workspace
  // ---------------------------------------------------------------------------
  GRID_SYSTEM: {
    MIN_TILE_SIZE: 24,
    MAX_TILE_SIZE: 60,
    PREFERRED_TILE_SIZE: 40,
    MIN_GRID_COLS: 12,
    MAX_GRID_COLS: 24,
    MIN_GRID_ROWS: 8,
    MAX_GRID_ROWS: 16,
    PREFERRED_GRID_COLS: 18,
    PREFERRED_GRID_ROWS: 12,
    PADDING_RATIO: 0.02,
    MIN_PADDING: 8,
    MAX_PADDING: 24,
    STATION_SPACING_RATIO: 0.08,
    CENTER_BIAS: 0.7,
    // Mobile-specific grid settings
    MOBILE_TILE_SIZE: 16,
    MOBILE_GRID_COLS: 32,
    MOBILE_GRID_ROWS: 24,
    MOBILE_PADDING: 4,
    SMALL_MOBILE_TILE_SIZE: 14,
    LANDSCAPE_MOBILE_TILE_SIZE: 12,
  },

  // ---------------------------------------------------------------------------
  // SEED GENERATION SYSTEM
  // ---------------------------------------------------------------------------
  SEED_GENERATION: {
    PREFIX: 'CA', // Coffee Alchemy prefix for auto-generated seeds
    RANDOM_RANGE: 999999, // Range for random component
    COINS_DIVISOR: 100, // Divide coins by this for compact representation
    XP_DIVISOR: 10, // Divide XP by this for compact representation
    MULTIPLIER: 1664525, // Linear congruential generator multiplier
    INCREMENT: 1013904223, // Linear congruential generator increment
    MODULUS: 4294967296, // Linear congruential generator modulus (2^32)
    MAX_SEED_LENGTH: 200, // Maximum allowed seed length for validation
    MIN_SEED_LENGTH: 3, // Minimum allowed seed length for validation
  },

  // ---------------------------------------------------------------------------
  // INVENTORY CATEGORIES
  // ---------------------------------------------------------------------------
  INVENTORY_CATEGORIES: {
    INGREDIENTS: {
      id: 'ingredients',
      name: 'Ingredients',
      types: ['ingredient']
    },
    TOOLS: {
      id: 'tools', 
      name: 'Tools',
      types: ['tool']
    },
    DRINKS: {
      id: 'drinks',
      name: 'Drinks', 
      types: ['beverage', 'dessert-beverage']
    }
  },

  // ---------------------------------------------------------------------------
  // CORE GAMEPLAY & ECONOMY
  // ---------------------------------------------------------------------------
  MAX_QUEUE_SIZE: 6,
  LEVEL_XP_THRESHOLDS: {
    1: 0, 2: 20, 3: 50, 4: 90, 5: 140, 6: 200, 7: 270, 8: 350, 9: 440, 10: 540,
    11: 650, 12: 770, 13: 900, 14: 1040, 15: 1200, 16: 1370, 17: 1550, 18: 1740, 19: 1940, 20: 2150,
  },
  XP_REWARDS: {
    simple: 1,
    medio: 2,
    premium: 3,
  },
  SIMPLE_DRINKS: ['americano', 'espresso'],
  PREMIUM_DRINKS: [
    'latte_art_latte', 'espresso_con_panna', 'affogato', 'sundae_affogato',
    'mocha', 'peppermint_mocha', 'dirty_matcha'
  ],
  ESPRESSO_BASED_DRINKS: [
    'espresso', 'americano', 'latte', 'cappuccino', 'mocha', 
    'espresso_con_panna', 'flat_white', 'sweet_espresso', 'spanish_latte',
    'cardamom_espresso', 'turkish_coffee'
  ],

  // ---------------------------------------------------------------------------
  // REPUTATION SYSTEM
  // ---------------------------------------------------------------------------
  REPUTATION_CONFIG: {
    INITIAL_REPUTATION: 50,
    MIN_REPUTATION: 0,
    MAX_REPUTATION: 100,
    GAINS: {
      SERVE_REGULAR: 1,
      SERVE_PREMIUM: 2,
      SERVE_CRITIC_SUCCESS: 10,
      PERFECT_LATTE_ART: 1, // Bonus point
    },
    LOSSES: {
      CUSTOMER_LEAVES: -5,
      SERVE_WRONG_ITEM: -2, // For future implementation
      CRITIC_FAILURE: -15,
    },
    LEVELS: {
      LOVED: { threshold: 90, name: 'Loved', color: '#22c55e' }, // green-500
      LIKED: { threshold: 70, name: 'Liked', color: '#84cc16' }, // lime-500
      NEUTRAL: { threshold: 40, name: 'Neutral', color: '#a1a1aa' }, // zinc-400
      DISLIKED: { threshold: 20, name: 'Disliked', color: '#f97316' }, // orange-500
      HATED: { threshold: 0, name: 'Hated', color: '#ef4444' }, // red-500
    }
  },

  // ---------------------------------------------------------------------------
  // LEVELING & UNLOCKS
  // ---------------------------------------------------------------------------
  LEVEL_UNLOCKS: {
    2: { items: { cocoa_powder: 5 } },
    3: { items: { vanilla: 5 } },
    4: { items: { shaker: 1 } },
    5: { items: { ice_cream: 3 } },
    6: { items: { turkish_grinder: 1 } },
  },

  // ---------------------------------------------------------------------------
  // SHOP
  // ---------------------------------------------------------------------------
  SHOP_ITEMS: {
    water: { price: 2, maxPurchase: 20 },
    beans: { price: 5, maxPurchase: 15 },
    milk: { price: 3, maxPurchase: 15 },
    sugar: { price: 2, maxPurchase: 20 },
    ice: { price: 1, maxPurchase: 25 },
    cocoa_powder: { price: 8, maxPurchase: 10, unlockLevel: 2 },
    vanilla: { price: 10, maxPurchase: 8, unlockLevel: 3 },
    ice_cream: { price: 12, maxPurchase: 6, unlockLevel: 5 },
    cinnamon: { price: 7, maxPurchase: 10, unlockLevel: 3 },
    cardamom: { price: 9, maxPurchase: 8, unlockLevel: 3 },
    nutmeg: { price: 7, maxPurchase: 10, unlockLevel: 3 },
    oat_milk: { price: 4, maxPurchase: 10, unlockLevel: 4 },
    almond_milk: { price: 4, maxPurchase: 10, unlockLevel: 4 },
    coconut_milk: { price: 5, maxPurchase: 10, unlockLevel: 4 },
    rose_petals: { price: 15, maxPurchase: 5, unlockLevel: 5 },
    tahini: { price: 12, maxPurchase: 5, unlockLevel: 5 },
    honey: { price: 8, maxPurchase: 10, unlockLevel: 5 },
    black_sesame_paste: { price: 14, maxPurchase: 5, unlockLevel: 5 },
    matcha: { price: 18, maxPurchase: 5, unlockLevel: 6 },
    pumpkin_spice: { price: 10, maxPurchase: 10 },
    peppermint: { price: 10, maxPurchase: 10 },
    saffron: { price: 25, maxPurchase: 3, prestige: 1 },
    ube: { price: 20, maxPurchase: 5, prestige: 2 },
    brew_station_2: { price: 500, maxPurchase: 1, unlockLevel: 5 },
  },

  // ---------------------------------------------------------------------------
  // EQUIPMENT UPGRADES
  // ---------------------------------------------------------------------------
  EQUIPMENT_UPGRADES: {
    grinder: {
      name: "Grinder",
      levels: {
        1: { cost: 0, description: "Standard grinding speed." },
        2: { cost: 250, unlockLevel: 3, description: "Grinds 15% faster. Unlocks fine grind." },
        3: { cost: 750, unlockLevel: 6, description: "Grinds 30% faster. Improves ground quality." }
      }
    },
    espresso_machine: {
      name: "Espresso Machine",
      levels: {
        1: { cost: 0, description: "Standard extraction." },
        2: { cost: 300, unlockLevel: 4, description: "Pulls shots 10% faster. Better heat retention." },
        3: { cost: 800, unlockLevel: 7, description: "Pulls shots 25% faster. Improves espresso base quality." }
      }
    }
  },

  // ---------------------------------------------------------------------------
  // RECIPE SHOP
  // ---------------------------------------------------------------------------
  RECIPE_SHOP_ITEMS: {
    // Basic recipes (level 1-2)
    milk_steamer: { price: 15, unlockLevel: 1, name: "Steamed Milk Recipe" },
    steamed_milk_steamer: { price: 20, unlockLevel: 1, name: "Milk Foam Recipe" },
    sugar_water: { price: 10, unlockLevel: 1, name: "Simple Syrup Recipe" },
    
    // Intermediate recipes (level 2-4)
    espresso_steamed_milk: { price: 25, unlockLevel: 2, name: "Flat White Recipe" },
    steamed_milk_milk_foam: { price: 30, unlockLevel: 2, name: "Cappuccino Recipe" },
    latte_milk_foam: { price: 35, unlockLevel: 2, name: "Latte Art Recipe" },
    simple_syrup_steamer: { price: 25, unlockLevel: 2, name: "Caramel Syrup Recipe" },
    
    // Advanced recipes (level 3-5)
    cocoa_powder_simple_syrup: { price: 40, unlockLevel: 3, name: "Chocolate Syrup Recipe" },
    chocolate_syrup_milk: { price: 45, unlockLevel: 3, name: "Chocolate Milk Recipe" },
    chocolate_milk_espresso: { price: 50, unlockLevel: 3, name: "Mocha Recipe" },
    milk_shaker: { price: 35, unlockLevel: 4, name: "Whipped Cream Recipe" },
    espresso_whipped_cream: { price: 40, unlockLevel: 4, name: "Espresso Con Panna Recipe" },
    simple_syrup_vanilla: { price: 45, unlockLevel: 3, name: "Vanilla Syrup Recipe" },
    
    // Iced recipes (level 4+)
    americano_ice: { price: 30, unlockLevel: 4, name: "Iced Americano Recipe" },
    latte_ice: { price: 35, unlockLevel: 4, name: "Iced Latte Recipe" },
    espresso_ice: { price: 25, unlockLevel: 4, name: "Iced Espresso Recipe" },
    ground_coffee_ice: { price: 40, unlockLevel: 4, name: "Cold Brew Recipe" },
    cold_brew_milk: { price: 45, unlockLevel: 4, name: "Cold Brew Latte Recipe" },
    
    // Dessert recipes (level 5+)
    espresso_ice_cream: { price: 60, unlockLevel: 5, name: "Affogato Recipe" },
    affogato_chocolate_syrup: { price: 75, unlockLevel: 5, name: "Sundae Affogato Recipe" },
    
    // Spice recipes (level 3+)
    simple_syrup_cinnamon: { price: 50, unlockLevel: 3, name: "Cinnamon Syrup Recipe" },
    latte_cinnamon_syrup: { price: 55, unlockLevel: 3, name: "Cinnamon Latte Recipe" },
    espresso_cardamom: { price: 60, unlockLevel: 3, name: "Cardamom Espresso Recipe" },
    cappuccino_nutmeg: { price: 55, unlockLevel: 3, name: "Nutmeg Cappuccino Recipe" },
    
    // Alt milk recipes (level 4+)
    latte_oat_milk: { price: 50, unlockLevel: 4, name: "Oat Milk Latte Recipe" },
    cappuccino_almond_milk: { price: 50, unlockLevel: 4, name: "Almond Cappuccino Recipe" },
    iced_latte_coconut_milk: { price: 55, unlockLevel: 4, name: "Iced Coconut Latte Recipe" },
    
    // Seasonal recipes (level 3+)
    simple_syrup_pumpkin_spice: { price: 60, unlockLevel: 3, name: "Pumpkin Spice Syrup Recipe" },
    latte_pumpkin_spice_syrup: { price: 65, unlockLevel: 3, name: "Pumpkin Spice Latte Recipe" },
    mocha_peppermint: { price: 70, unlockLevel: 3, name: "Peppermint Mocha Recipe" },
    
    // International recipes (level 5+)
    simple_syrup_rose_petals: { price: 80, unlockLevel: 5, name: "Rose Syrup Recipe" },
    latte_rose_syrup: { price: 85, unlockLevel: 5, name: "Rose Latte Recipe" },
    tahini_honey: { price: 75, unlockLevel: 5, name: "Tahini Honey Syrup Recipe" },
    latte_tahini_honey_syrup: { price: 90, unlockLevel: 5, name: "Tahini Honey Latte Recipe" },
    
    // Fusion recipes (level 6+)
    beans_turkish_grinder: { price: 70, unlockLevel: 6, name: "Turkish Grind Recipe" },
    turkish_grind_water: { price: 100, unlockLevel: 6, name: "Turkish Coffee Recipe" },
    matcha_water: { price: 90, unlockLevel: 6, name: "Matcha Shot Recipe" },
    matcha_shot_espresso: { price: 120, unlockLevel: 6, name: "Dirty Matcha Recipe" },
    
    // Prestige recipes
    latte_saffron: { price: 150, prestige: 1, name: "Saffron Latte Recipe" },
    latte_ube: { price: 130, prestige: 2, name: "Ube Latte Recipe" },
  },

  // ---------------------------------------------------------------------------
  // TUTORIALS - Updated level gates for better progression
  // ---------------------------------------------------------------------------
  TUTORIALS: {
    onboarding: {
      id: 'onboarding', levelGate: 1, title: 'Welcome to Coffee Alchemy!',
      steps: [
        { text: "Let's make your first coffee! Select Coffee Beans from your inventory.", highlightQuery: "[data-tutorial-id='item-beans']", action: { type: 'selectItem', itemId: 'beans' } },
        { text: "Now select the Grinder tool to grind the beans.", highlightQuery: "[data-tutorial-id='item-grinder']", action: { type: 'selectItem', itemId: 'grinder' } },
        { text: "Great! Press BREW to make Ground Coffee.", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['beans', 'grinder'] } },
        { text: "By the way, you can click and drag this brew station to move it around your workspace!", highlightQuery: "[data-tutorial-id='brew-station-0']" },
        { text: "Now, let's make an Espresso. Select your new Ground Coffee.", highlightQuery: "[data-tutorial-id='item-ground_coffee']", action: { type: 'selectItem', itemId: 'ground_coffee' } },
        { text: "And select the Espresso Machine.", highlightQuery: "[data-tutorial-id='item-espresso_machine']", action: { type: 'selectItem', itemId: 'espresso_machine' } },
        { text: "Brew them together to make an Espresso!", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['ground_coffee', 'espresso_machine'] } },
        { text: "A customer is here for an Espresso! Click 'Serve' to give them their order.", highlightQuery: "[data-tutorial-id='serve-button-espresso']", action: { type: 'serve', orderId: 'espresso' }, spawnOrder: 'espresso' },
        { text: "Perfect! Now explore the game: Use the Shop to buy ingredients, check Challenges for goals, view the Recipe Book for discovered combinations, and adjust Settings. Experiment with different ingredient combinations to discover new recipes!", highlightQuery: "[data-tutorial-id='recipe-book-button']" },
      ]
    },
    americano_basics: {
      id: 'americano_basics', levelGate: 2, title: 'Making an Americano',
      steps: [
        { text: "Let's learn to make an Americano! First, we need an Espresso. Select Ground Coffee from your inventory.", highlightQuery: "[data-tutorial-id='item-ground_coffee']", action: { type: 'selectItem', itemId: 'ground_coffee' } },
        { text: "Now select the Espresso Machine.", highlightQuery: "[data-tutorial-id='item-espresso_machine']", action: { type: 'selectItem', itemId: 'espresso_machine' } },
        { text: "Brew them to make an Espresso.", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['ground_coffee', 'espresso_machine'] } },
        { text: "Great! Now to make an Americano, we add hot water to the espresso. Select your Espresso.", highlightQuery: "[data-tutorial-id='item-espresso']", action: { type: 'selectItem', itemId: 'espresso' } },
        { text: "Now select Water from your inventory.", highlightQuery: "[data-tutorial-id='item-water']", action: { type: 'selectItem', itemId: 'water' } },
        { text: "Brew them together to create an Americano!", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['espresso', 'water'] } },
        { text: "Perfect! A customer wants an Americano. Serve them your freshly made drink!", highlightQuery: "[data-tutorial-id='serve-button-americano']", action: { type: 'serve', orderId: 'americano' }, spawnOrder: 'americano' },
        { text: "Excellent! You've mastered the Americano - a classic coffee drink that's perfect for those who like their coffee strong but not as intense as straight espresso.", isEnd: true },
      ]
    },
    latte_basics: {
      id: 'latte_basics', levelGate: 2, title: 'Making a Latte',
      steps: [
        { text: "Time to learn the Latte! This creamy coffee drink starts with an espresso base. Select Ground Coffee.", highlightQuery: "[data-tutorial-id='item-ground_coffee']", action: { type: 'selectItem', itemId: 'ground_coffee' } },
        { text: "Select the Espresso Machine to brew the espresso.", highlightQuery: "[data-tutorial-id='item-espresso_machine']", action: { type: 'selectItem', itemId: 'espresso_machine' } },
        { text: "Brew them to make your Espresso base.", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['ground_coffee', 'espresso_machine'] } },
        { text: "Now for the creamy part! A Latte combines espresso with milk. Select your Espresso.", highlightQuery: "[data-tutorial-id='item-espresso']", action: { type: 'selectItem', itemId: 'espresso' } },
        { text: "Now select Milk from your inventory.", highlightQuery: "[data-tutorial-id='item-milk']", action: { type: 'selectItem', itemId: 'milk' } },
        { text: "Brew them together to create a smooth, creamy Latte!", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['espresso', 'milk'] } },
        { text: "Wonderful! A customer is waiting for a Latte. Serve them this delicious coffee creation!", highlightQuery: "[data-tutorial-id='serve-button-latte']", action: { type: 'serve', orderId: 'latte' }, spawnOrder: 'latte' },
        { text: "Fantastic! You've mastered the Latte - one of the most popular coffee drinks worldwide. The combination of rich espresso and creamy milk creates the perfect balance.", isEnd: true },
      ]
    },
    milk_basics: {
      id: 'milk_basics', levelGate: 2, title: 'How to Make Cappuccinos',
      steps: [
        { text: "Let's make a Cappuccino. First, we need Milk Foam. Select Milk.", highlightQuery: "[data-tutorial-id='item-milk']", action: { type: 'selectItem', itemId: 'milk' } },
        { text: "Now select the Steamer tool.", highlightQuery: "[data-tutorial-id='item-steamer']", action: { type: 'selectItem', itemId: 'steamer' } },
        { text: "Brew them to create Steamed Milk.", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['milk', 'steamer'] } },
        { text: "Great! Now, steam the Steamed Milk again to create Milk Foam.", highlightQuery: "[data-tutorial-id='item-steamed_milk']", action: { type: 'selectItem', itemId: 'steamed_milk' } },
        { text: "Select the Steamer again.", highlightQuery: "[data-tutorial-id='item-steamer']", action: { type: 'selectItem', itemId: 'steamer' } },
        { text: "Brew to get Milk Foam.", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['steamed_milk', 'steamer'] } },
        { text: "Now to make the cappuccino itself. Select Steamed Milk.", highlightQuery: "[data-tutorial-id='item-steamed_milk']", action: { type: 'selectItem', itemId: 'steamed_milk' } },
        { text: "And now select Milk Foam.", highlightQuery: "[data-tutorial-id='item-milk_foam']", action: { type: 'selectItem', itemId: 'milk_foam' } },
        { text: "Brew them together to create a Cappuccino!", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['steamed_milk', 'milk_foam'] } },
        { text: "Perfect! A customer has arrived for a Cappuccino. Serve them!", highlightQuery: "[data-tutorial-id='serve-button-cappuccino']", action: { type: 'serve', orderId: 'cappuccino' }, spawnOrder: 'cappuccino' },
        { text: "Excellent! You've mastered Cappuccinos.", isEnd: true },
      ]
    },
    syrup_basics: {
      id: 'syrup_basics', levelGate: 3, title: 'How to Make Syrups',
      steps: [
        { text: "Let's make a Vanilla Latte. First, we need Vanilla Syrup. Select Sugar.", highlightQuery: "[data-tutorial-id='item-sugar']", action: { type: 'selectItem', itemId: 'sugar' } },
        { text: "Now select Water.", highlightQuery: "[data-tutorial-id='item-water']", action: { type: 'selectItem', itemId: 'water' } },
        { text: "Brew them to create Simple Syrup.", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['sugar', 'water'] } },
        { text: "Now, let's flavor it. Select your new Simple Syrup.", highlightQuery: "[data-tutorial-id='item-simple_syrup']", action: { type: 'selectItem', itemId: 'simple_syrup' } },
        { text: "Select Vanilla from your inventory.", highlightQuery: "[data-tutorial-id='item-vanilla']", action: { type: 'selectItem', itemId: 'vanilla' } },
        { text: "Brew to get Vanilla Syrup.", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['simple_syrup', 'vanilla'] } },
        { text: "A customer wants a Vanilla Latte! Select a Latte.", highlightQuery: "[data-tutorial-id='item-latte']", action: { type: 'selectItem', itemId: 'latte' }, spawnOrder: 'vanilla_latte' },
        { text: "And now your new Vanilla Syrup.", highlightQuery: "[data-tutorial-id='item-vanilla_syrup']", action: { type: 'selectItem', itemId: 'vanilla_syrup' } },
        { text: "Brew the Vanilla Latte!", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['latte', 'vanilla_syrup'] } },
        { text: "Serve the happy customer!", highlightQuery: "[data-tutorial-id='serve-button-vanilla_latte']", action: { type: 'serve', orderId: 'vanilla_latte' } },
        { text: "You're a syrup master!", isEnd: true },
      ]
    },
    latte_art: {
      id: 'latte_art', levelGate: 3, title: 'Latte Art Basics',
      steps: [
        { text: "For some drinks like Lattes and Cappuccinos, a button will appear to create Latte Art.", highlightQuery: "[data-tutorial-id='latte-art-button-placeholder']" },
        { text: "Clicking it starts a mini-game. Draw a steady line to get a 'Good' or 'Perfect' score.", highlightQuery: "[data-tutorial-id='latte-art-button-placeholder']" },
        { text: "Successful latte art gives you a big tip bonus on that drink!", isEnd: true },
      ]
    },
    iced_drinks: {
      id: 'iced_drinks', levelGate: 4, title: 'Making Iced Drinks',
      steps: [
        { text: "Let's cool things down! You can make an iced version of many drinks. First, make a Latte.", highlightQuery: "[data-tutorial-id='item-latte']" },
        { text: "Now, select your Latte.", highlightQuery: "[data-tutorial-id='item-latte']", action: { type: 'selectItem', itemId: 'latte' } },
        { text: "And select Ice.", highlightQuery: "[data-tutorial-id='item-ice']", action: { type: 'selectItem', itemId: 'ice' } },
        { text: "Brew them to create an Iced Latte!", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['latte', 'ice'] } },
        { text: "A customer is here for a refreshing Iced Latte. Serve them!", highlightQuery: "[data-tutorial-id='serve-button-iced_latte']", action: { type: 'serve', orderId: 'iced_latte' }, spawnOrder: 'iced_latte' },
        { text: "Great job! Try adding ice to other drinks to see what you discover.", isEnd: true },
      ]
    },
    desserts: {
      id: 'desserts', levelGate: 5, title: 'Dessert Time!',
      steps: [
        { text: "You've unlocked Ice Cream! Let's make a classic dessert: the Affogato.", highlightQuery: "[data-tutorial-id='item-ice_cream']" },
        { text: "Select an Espresso.", highlightQuery: "[data-tutorial-id='item-espresso']", action: { type: 'selectItem', itemId: 'espresso' } },
        { text: "Now select Ice Cream.", highlightQuery: "[data-tutorial-id='item-ice_cream']", action: { type: 'selectItem', itemId: 'ice_cream' } },
        { text: "Brew them to create an Affogato. This will also start a RUSH HOUR event!", highlightQuery: "[data-tutorial-id='brew-button-0']", action: { type: 'brew', ingredients: ['espresso', 'ice_cream'] } },
        { text: "A customer wants an Affogato! Serve them!", highlightQuery: "[data-tutorial-id='serve-button-affogato']", action: { type: 'serve', orderId: 'affogato' }, spawnOrder: 'affogato' },
        { text: "Rush Hour is on! Serve customers quickly for extra tips.", isEnd: true },
      ]
    },
    seasonal_drinks: {
      id: 'seasonal_drinks', levelGate: 4, title: 'Seasonal Specialties!',
      steps: [
        { text: "A new season has arrived! Check the header bar to see which season it is.", highlightQuery: "[data-tutorial-id='stats-bar']" },
        { text: "During each season, special ingredients are available in the shop. Look for items with a seasonal tag!", highlightQuery: "[data-tutorial-id='shop-button']" },
        { text: "Crafting drinks with these ingredients creates seasonal beverages. Customers love them and will pay a tip bonus!", highlightQuery: "[data-tutorial-id='recipe-book-button']" },
        { text: "Keep an eye on the seasons to discover all the special recipes and maximize your profits!", isEnd: true },
      ]
    },
  },

  // ---------------------------------------------------------------------------
  // CUSTOMERS & EVENTS
  // ---------------------------------------------------------------------------
  CUSTOMER_NAMES: [
    "Alex", "Ben", "Chloe", "David", "Eva", "Finn", "Grace", "Henry", "Isla", "Jack",
    "Kara", "Leo", "Mia", "Noah", "Olivia", "Paul", "Quinn", "Ruby", "Sam", "Tara",
    "Uma", "Vince", "Willow", "Xavi", "Yara", "Zane"
  ],
  CUSTOMER_SPAWN_CONFIG: {
    baseRate: 4500, minRate: 3200, maxRate: 5000, levelModifier: 200,
  },
  CUSTOMER_PATIENCE: {
    regular: { min: 24, max: 38 },
    kid: { min: 18, max: 26 },
    critic: { min: 45, max: 60 },
  },
  KID_SPAWN_CHANCE: 0.3,
  SEASONAL_ORDER_CHANCE: 0.3,
  AFFOGATO_EVENT: {
    duration: 45000, spawnRateMultiplier: 1.5, tipBonus: 0.10, globalCooldown: 180000,
  },
  DAILY_BLEND: {
    duration: 600000, tipBonus: 0.25,
    eligibleDrinks: [
      'americano', 'latte', 'cappuccino', 'mocha', 'iced_latte', 
      'espresso_con_panna', 'vanilla_latte', 'caramel_latte', 'cold_brew_latte'
    ]
  },
  CRITIC_VISIT: {
    triggerCount: 10, duration: 90000, cooldown: 600000,
    orderSequence: ['espresso', 'mocha', 'affogato'],
    requiredLatteArt: true
  },
  CUSTOMER_PREFERENCES: {
    TAGS: ['sweet', 'iced', 'strong', 'spice', 'dessert'],
    TIP_BONUS: 0.10,
    CHANCE: 0.4,
  },
  TREND_SYSTEM: {
    DURATION: 300000,
    TIP_BONUS: 0.20,
    UPDATE_INTERVAL: 60000,
    CHANCE: 0.5,
  },

  // ---------------------------------------------------------------------------
  // MINI-GAMES & MODES
  // ---------------------------------------------------------------------------
  LATTE_ART: {
    showDuration: 3000, gestureDuration: 2000,
    perfectThreshold: 90, goodThreshold: 70,
    perfectBonus: 0.20, goodBonus: 0.10,
    buffDuration: 180000, // 3 minutes
    applicableDrinks: ['latte', 'latte_art_latte', 'cappuccino', 'mocha']
  },
  RUSH_MODE: {
    duration: 180000, // 3 minutes
    spawnRateMultiplier: 2.0, // Double the spawn rate
    patienceMultiplier: 0.7, // Reduce patience by 30%
    maxQueueMultiplier: 1.5, // Allow 50% more customers in queue
    scoreMultipliers: { 
      served: 10, 
      tips: 1, 
      combos: 3,
      legendary: 20,
      uniqueCategory: 5,
    },
    tipBonus: 0.15, // 15% bonus tips during rush mode
  },

  // ---------------------------------------------------------------------------
  // NOTIFICATION SYSTEM
  // ---------------------------------------------------------------------------
  NOTIFICATION_DURATION: 3000, // 3 seconds for all notifications
  DISCOVERY_NOTIFICATION: {
    duration: 3000, // 3 seconds display time
  },

  // ---------------------------------------------------------------------------
  // QUESTS
  // ---------------------------------------------------------------------------
  QUEST_POOL: [
    { id: 'serve_iced', description: 'Serve 5 Iced drinks', target: 5, reward: { type: 'coins', amount: 30 }, trackingKey: 'icedServed' },
    { id: 'discover_combos', description: 'Discover 2 new combos', target: 2, reward: { type: 'xp', amount: 10 }, trackingKey: 'combosDiscovered' },
    { id: 'perfect_latte_art', description: 'Perfect a Latte Art', target: 1, reward: { type: 'buff', duration: 180000, bonus: 0.10 }, trackingKey: 'perfectLatteArt' },
    { id: 'serve_premium', description: 'Serve 3 Premium drinks', target: 3, reward: { type: 'coins', amount: 50 }, trackingKey: 'premiumServed' },
    { id: 'fast_service', description: 'Serve 5 customers quickly', target: 5, reward: { type: 'xp', amount: 15 }, trackingKey: 'fastService' },
    { id: 'serve_espresso_based', description: 'Serve 8 espresso-based drinks', target: 8, reward: { type: 'coins', amount: 40 }, trackingKey: 'espressoBasedServed' },
    { id: 'perfect_temp_hit', description: 'Hit optimal temperature 5 times', target: 5, reward: { type: 'buff', duration: 300000, bonus: 0.15 }, trackingKey: 'perfectTempHit' },
    { id: 'serve_spice', description: 'Spice Lord: Serve 10 spiced drinks', target: 10, reward: { type: 'coins', amount: 75 }, trackingKey: 'spiceServed' },
    { id: 'serve_alt_milk', description: 'Alt-Milk Maestro: Serve 10 alt-milk drinks', target: 10, reward: { type: 'xp', amount: 25 }, trackingKey: 'altMilkServed' },
    { id: 'serve_seasonal', description: 'Season Slayer: Serve 10 seasonal drinks', target: 10, reward: { type: 'coins', amount: 75 }, trackingKey: 'seasonalServed' },
    { id: 'serve_fusion', description: 'Fusionist: Serve 10 fusion drinks', target: 10, reward: { type: 'xp', amount: 25 }, trackingKey: 'fusionServed' },
  ],

  // ---------------------------------------------------------------------------
  // AI & DYNAMIC SYSTEMS
  // ---------------------------------------------------------------------------
  AI_BARISTA: {
    UNLOCK_LEVEL: 8,
    UNLOCK_COST: 1000,
    MAX_CRAFTING_DEPTH: 15,
    LEVELS: {
      0: { speed: 5000, efficiency: 0, cost: 0, name: "Not Purchased" },
      1: { speed: 4000, efficiency: 0.05, cost: 2000, name: "Level 1" }, // 5% chance to not consume ingredient
      2: { speed: 3000, efficiency: 0.10, cost: 5000, name: "Level 2" },
      3: { speed: 2000, efficiency: 0.15, cost: 10000, name: "Level 3" },
      4: { speed: 1000, efficiency: 0.20, cost: 25000, name: "Level 4" },
    }
  },
  AI_TIPS: {
    triggerFailures: 2, triggerTimeouts: 2, timeoutWindow: 30000,
    hints: [
      "Try combining basic ingredients first - Water + Beans is a good start!",
      "The Grinder turns Beans into Ground Coffee for brewing.",
      "Espresso Machine + Ground Coffee makes the base for many drinks.",
      "Add Milk to Espresso for a classic Latte.",
      "Ice can cool down any hot beverage for variety.",
      "Simple Syrup adds sweetness - try Sugar + Water first.",
      "Steam Milk for better texture in coffee drinks.",
      "Premium drinks like Mocha need Chocolate Syrup."
    ]
  },
  AI_RECIPE_GEN: {
    TIMEOUT_MS: 500,
    VALID_TAGS: ['spice', 'alt-milk', 'seasonal', 'fusion', 'sweet', 'iced', 'strong', 'dessert', 'legendary'],
    PROFANITY_FILTER: ['badword', 'profanity', 'inappropriate'],
    DEFAULT_PRICE: 10,
  },
  MOCK_AI_RESPONSES: [
    { verdict: "accept", name: "Synergistic Sip", type: "beverage", unlocks: [], constraints_respected: true, note: "An interesting combination that somehow works.", tags: ["sweet"] },
    { verdict: "reject", name: "", type: "", unlocks: [], constraints_respected: true, note: "These ingredients don't seem to work well together.", tags: [] },
    { verdict: "hint", name: "", type: "", unlocks: [], constraints_respected: true, note: "You're on the right track! Try adding something sweet.", tags: [] },
    { verdict: "accept", name: "Spicy Water", type: "beverage", unlocks: [], constraints_respected: true, note: "It's... surprisingly tingly.", tags: ["spice"] },
  ],
  TASTE_PROFILE: {
    sweet: 0, spice: 0, dairy: 0, iced: 0, strong: 0, alt_milk: 0, seasonal: 0, fusion: 0, dessert: 0,
  },
  CUSTOMER_AI: {
    HISTORY_LENGTH: 3, PREFERENCE_UPDATE_WEIGHT: 0.25, ORDER_BIAS_STRENGTH: 1.5, MONOTONY_PENALTY: 0.5,
  },
  TREND_PREDICTION: {
    UPDATE_INTERVAL: 120000, ORDER_POOL_BIAS: 1.1, TOP_N_SERVED: 5,
  },
  DYNAMIC_DIFFICULTY: {
    EVALUATION_WINDOW: 300000, EASY_THRESHOLD: 2, HARD_THRESHOLD: 8,
    EASY_MODIFIERS: { windowMultiplier: 1.2, spawnTimeModifier: -200, },
    HARD_MODIFIERS: { windowMultiplier: 0.9, spawnTimeModifier: 200, },
  },
  ORDER_POOL_WEIGHTS: {
    LEVEL_TIER_BONUS: 0.05, // bonus per level above unlock
    RECENT_ORDER_PENALTY: 0.2, // multiplier for last 2 served items
    HISTORY_LENGTH: 5, // how many items to consider for penalty
  },

  // ---------------------------------------------------------------------------
  // LEGENDARY SYSTEMS & ADVANCED MECHANICS
  // ---------------------------------------------------------------------------
  PRESTIGE: {
    REQUIREMENTS: {
      level: 10,
      legendaries: 2,
    },
    LEVELS: {
      1: {
        ui: 'I',
        unlockItem: 'saffron',
        perk: { type: 'tip_bonus', category: 'spice', value: 0.05 },
        description: "Unlock Saffron. +5% tips on all spiced drinks."
      },
      2: {
        ui: 'II',
        unlockItem: 'ube',
        perk: { type: 'timing_window_increase', category: 'dessert-beverage', value: 1000 },
        description: "Unlock Ube. +1s timing window on dessert-beverages."
      },
    }
  },
  ROTATING_SEASONS: {
    ROTATION_DURATION_DAYS: 7,
    SEASON_ORDER: ['FALL', 'WINTER', 'SPRING', 'SUMMER'],
    DEFINITIONS: {
      FALL: { name: 'Autumn Harvest', item: 'pumpkin_spice', tipBonus: 0.10, ui_color: 'bg-orange-500' },
      WINTER: { name: 'Winter Wonderland', item: 'peppermint', tipBonus: 0.10, ui_color: 'bg-sky-500' },
      SPRING: { name: 'Spring Bloom', item: 'rose_petals', tipBonus: 0.10, ui_color: 'bg-pink-500' },
      SUMMER: { name: 'Summer Festival', item: 'coconut_milk', tipBonus: 0.10, ui_color: 'bg-yellow-400' },
    }
  },
  MASTER_CHEF_CHALLENGES: {
    perfect_temp: {
      id: 'perfect_temp',
      name: 'Perfect Temperature',
      description: 'Hit the optimal temperature range 5 times in 2 minutes.',
      duration: 120000,
      goal: 5,
      trackingKey: 'perfectTempHit',
      reward: { type: 'coins', amount: 100 },
      prerequisites: {
        unlocks: 'temperatureControl'
      }
    },
    art_sprint: {
      id: 'art_sprint',
      name: 'Art Sprint',
      description: 'Create 3 Perfect Latte Arts in 3 minutes.',
      duration: 180000,
      goal: 3,
      trackingKey: 'perfectLatteArt',
      reward: { type: 'buff', buffType: 'temp_window', value: 1.2, duration: 600000, description: "+20% wider temp window for 10 mins" },
      prerequisites: {
        level: 3,
        recipe: 'latte_art_latte'
      }
    },
    iced_frenzy: {
      id: 'iced_frenzy',
      name: 'Iced Frenzy',
      description: 'Serve 10 iced drinks in 5 minutes.',
      duration: 300000,
      goal: 10,
      trackingKey: 'icedServed',
      reward: { type: 'coins', amount: 150 },
      prerequisites: {
        level: 4,
        item: 'ice'
      }
    }
  },
  LEGENDARY_UNLOCKS: {
    PHOENIX_BLEND: {
      id: 'phoenix_blend', name: 'Phoenix Blend', type: 'beverage', description: 'A legendary beverage that unlocks temperature control',
      sequence: ['espresso', 'cardamom_espresso', 'espresso_con_panna', 'dirty_matcha'],
      sequenceWindow: 300000, unlocks: 'TEMPERATURE_CONTROL', price: 50, consumable: true
    },
    AURORA_DRIP: {
      id: 'aurora_drip', name: 'Aurora Drip', type: 'beverage', description: 'A legendary beverage that unlocks environmental modifiers',
      triggerCondition: 'NIGHT_ICED_SEQUENCE', icedRequired: 3, finalDrink: 'cold_brew_latte',
      timeWindow: 60000, unlocks: 'ENVIRONMENTAL_MODIFIERS', price: 45, consumable: true
    },
    MASTERS_HARMONY: {
      id: 'masters_harmony', name: "Master's Harmony", type: 'dessert-beverage', description: 'A legendary dessert that unlocks quality grades',
      triggerCondition: 'LATTE_ART_SEQUENCE', perfectRequired: 2, goodRequired: 1,
      timeWindow: 90000, finalCraft: 'mocha', unlocks: 'QUALITY_GRADES', price: 60, consumable: true
    }
  },
  TEMPERATURE_CONTROL: {
    tools: ['espresso_machine', 'steamer', 'kettle'],
    defaultTemp: 85,
    optimalRanges: {
      espresso: { min: 92, max: 96, bonus: 0.15 },
      americano: { min: 88, max: 94, bonus: 0.10 },
      latte: { min: 65, max: 75, bonus: 0.12 },
      cappuccino: { min: 65, max: 75, bonus: 0.12 },
      mocha: { min: 70, max: 85, bonus: 0.10 },
      steamed_milk: { min: 65, max: 75, bonus: 0.08 },
      turkish_coffee: { min: 96, max: 98, bonus: 0.20 },
      dirty_matcha: { min: 70, max: 75, bonus: 0.18 }
    }
  },
  GAME_TIME: {
    DAY_DURATION: 600000,
    PERIOD_DEFINITIONS: { MORNING: 0.3, AFTERNOON: 0.4, NIGHT: 0.3, }
  },
  ENVIRONMENTAL_MODIFIERS: {
    HUMIDITY_CHANGE_CHANCE: 0.001,
    timeOfDay: {
      MORNING: { humidityBonus: 0, temperatureOffset: 0 },
      AFTERNOON: { humidityBonus: -0.05, temperatureOffset: 5 },
      NIGHT: { humidityBonus: 0.08, temperatureOffset: -10 }
    },
    humidity: {
      LOW: { threshold: 30, effect: 'Brew time -15%', brewTimeMultiplier: 0.85 },
      NORMAL: { threshold: 80, effect: 'Standard conditions', brewTimeMultiplier: 1.0 },
      HIGH: { threshold: 100, effect: 'Brew time +15%', brewTimeMultiplier: 1.15 }
    }
  },
  QUALITY_SCORING: {
    maxTempScore: 50, maxTimingScore: 30, maxTechniqueScore: 20, baseIngredientQuality: 70,
  },
  QUALITY_GRADES: {
    S: { threshold: 95, multiplier: 1.25, color: '#FFD700', name: 'Legendary' },
    A: { threshold: 85, multiplier: 1.15, color: '#C0C0C0', name: 'Excellent' },
    B: { threshold: 70, multiplier: 1.0, color: '#CD7F32', name: 'Good' },
    C: { threshold: 50, multiplier: 0.9, color: '#8B4513', name: 'Average' },
    F: { threshold: 0, multiplier: 0.75, color: '#696969', name: 'Poor' }
  },
  TIMING_WINDOWS: {
    americano: { required: 'espresso', window: 20000, score: 25 },
    latte: { required: 'espresso', window: 20000, score: 20 },
    cappuccino: { required: 'espresso', window: 15000, score: 30 },
    flat_white: { required: 'espresso', window: 15000, score: 25 },
    mocha: { required: 'chocolate_milk', window: 30000, score: 15 },
  },
  SEQUENCE_RECIPES: {
    rose_delight: {
      id: 'rose_delight',
      name: 'Rose Delight',
      description: 'A master recipe requiring perfect temperature and timing.',
      result: { id: "rose_delight", name: "Rose Delight", type: "beverage", description: "An exquisite floral latte.", price: 35, consumable: true },
      steps: [
        { action: 'brew', itemId: 'espresso', tempRange: [93, 95] },
        { action: 'combine', ingredientId: 'rose_syrup', window: 30000 }
      ],
      sequenceWindow: 45000 // 45s total to complete
    }
  },

  // ---------------------------------------------------------------------------
  // UI & MISC
  // ---------------------------------------------------------------------------
  AUDIO_SETTINGS: {
    default: { music: 0.5, sfx: 0.8 },
    event: { music: 0.8, sfx: 0.9 }
  },
  UI_MESSAGES: {
    DESSERT_STATION_UNLOCKED: '🎉 Dessert Station Unlocked! 🎉\nRush Hour activated!',
    LATTE_ART_SUCCESS: '{result} Latte Art! +{bonus}% tip bonus on this drink!',
    LATTE_ART_FAILURE: '{result} Latte Art. Try for a smoother line next time!',
    DAILY_BLEND_COMPLETE: '🎉 Daily Blend Complete! 🎉\n+{bonus}% tips for {duration} minutes!',
    CRITIC_VISIT_SUCCESS: '🎉 Critic Visit Success! 🎉\nNitro Tap Unlocked!\nNew nitrogen-infused beverages available!',
    CRITIC_VISIT_FAILURE: 'The critic was not impressed... Try again later.',
    CRITIC_VISIT_TIMEOUT: 'The critic ran out of time and left.',
    RUSH_MODE_OVER: 'Rush Mode Over!\n\nServed: {served}\nTips: {tips}\nCombos: {combos}\nLegendaries: {legendaries}\nCategories: {categories}\n\nFinal Score: {score}',
    SHARE_LINK_COPIED: 'Share link copied: {url}',
    FORCE_CRITIC_SUCCESS: "Critic visit conditions met. A critic will spawn shortly.",
    PHOENIX_BLEND_SEQUENCE_COMPLETE: '🔥 Phoenix Blend sequence complete! The AI will now create the legendary Phoenix Blend recipe...',
    AURORA_DRIP_CONDITIONS_MET: '🌌 Aurora conditions met! Craft a Cold Brew Latte within 60 seconds to unlock Aurora Drip!',
    AURORA_DRIP_UNLOCKED: '🌌 Aurora Drip unlocked! Environmental modifiers are now active!',
    MASTERS_HARMONY_CONDITIONS_MET: "🎨 Master's Harmony conditions met! Craft a Mocha to unlock the legendary Master's Harmony!",
    MASTERS_HARMONY_UNLOCKED: "🏆 Master's Harmony unlocked! Quality grades are now active!",
    AI_BARISTA_TIP: '💡 AI Barista Tip: {hint}',
    NEW_AI_DISCOVERY: 'New Discovery: {name}! {note}',
    AI_JUDGE_TIMEOUT: 'The AI seems to be thinking... a little too long. Try that combo again.',
    QUALITY_WARNING_POPUP: {
      title: 'Quality Warning',
      text: 'You served a C-grade item! This reduces your tips. Try to improve your technique by watching temperature and timing.'
    },
    PRESTIGE_UNLOCKED: 'You are eligible to Prestige! Check the settings panel.',
    PRESTIGE_CONFIRM: 'Are you sure you want to Prestige? This will reset your level, XP, coins, inventory, and recipe discoveries. You will keep your settings and gain a permanent perk.',
    PRESTIGE_COMPLETE: 'Congratulations on reaching Prestige {level}! {description}',
    CHALLENGE_START: 'Challenge Started: {name}! Good luck!',
    CHALLENGE_SUCCESS: 'Challenge Complete! You earned: {reward}.',
    CHALLENGE_FAILURE: 'Challenge Failed. Better luck next time!',
    RESET_GAME_CONFIRM_TITLE: 'Confirm Full Game Reset',
    RESET_GAME_CONFIRM_BODY: 'Are you sure you want to reset all progress? This will reset your level, XP, coins, inventory, and recipe discoveries.',
    RESET_GAME_CONFIRM_NOTE: 'Your prestige level and settings will be kept. Tutorial progress will be reset. This action cannot be undone.',
    SEED_VALIDATION: {
      INVALID_FORMAT: 'Invalid seed format. Please check your seed and try again.',
      TOO_LONG: 'Seed is too long. Maximum length is {maxLength} characters.',
      TOO_SHORT: 'Seed is too short. Minimum length is {minLength} characters.',
      CORRUPTED: 'Seed appears to be corrupted. Using default values.',
      SUCCESS: 'Seed applied successfully!',
      FALLBACK: 'Invalid seed - starting fresh game'
    }
  },
  TIME_WINDOWS: { // Legacy, for reference
    NIGHT_START: 20, NIGHT_END: 6, DAWN_START: 5, DAWN_END: 8
  },

  // ---------------------------------------------------------------------------
  // ITEM & RECIPE DATA
  // ---------------------------------------------------------------------------
  ITEM_TAGS: {
    water: [], beans: [], milk: ['dairy'], sugar: ['sweet'], ice: ['iced'], cocoa_powder: ['sweet'], vanilla: ['sweet'], ice_cream: ['dairy', 'sweet', 'dessert'], cinnamon: ['spice'], cardamom: ['spice'], nutmeg: ['spice'], oat_milk: ['alt_milk'], almond_milk: ['alt_milk'], coconut_milk: ['alt_milk'], rose_petals: ['fusion'], tahini: ['fusion'], honey: ['sweet'], black_sesame_paste: ['fusion'], matcha: ['fusion'], pumpkin_spice: ['seasonal', 'spice'], peppermint: ['seasonal', 'sweet'], ground_coffee: [], steamed_milk: ['dairy'], milk_foam: ['dairy'], simple_syrup: ['sweet'], caramel_syrup: ['sweet'], chocolate_syrup: ['sweet'], whipped_cream: ['dairy', 'sweet'], vanilla_syrup: ['sweet'], cinnamon_syrup: ['sweet', 'spice'], pumpkin_spice_syrup: ['sweet', 'seasonal', 'spice'], rose_syrup: ['sweet', 'fusion'], tahini_honey_syrup: ['sweet', 'fusion'], black_sesame_syrup: ['sweet', 'fusion'], turkish_grind: [], matcha_shot: ['fusion'],
    espresso: ['strong'], americano: ['strong'], flat_white: ['strong', 'dairy'], cappuccino: ['strong', 'dairy'], latte: ['strong', 'dairy'], latte_art_latte: ['strong', 'dairy'], sweet_espresso: ['strong', 'sweet'], spanish_latte: ['strong', 'sweet', 'dairy'], iced_americano: ['strong', 'iced'], iced_latte: ['strong', 'dairy', 'iced'], iced_espresso: ['strong', 'iced'], cold_brew: ['strong', 'iced'], cold_brew_latte: ['strong', 'dairy', 'iced'], sweet_cold_brew: ['strong', 'sweet', 'iced'], mocha: ['strong', 'sweet', 'dairy'], iced_mocha: ['strong', 'sweet', 'dairy', 'iced'], caramel_latte: ['strong', 'sweet', 'dairy'], caramel_cappuccino: ['strong', 'sweet', 'dairy'], affogato: ['strong', 'sweet', 'dessert', 'dairy'], sundae_affogato: ['strong', 'sweet', 'dessert', 'dairy'], espresso_con_panna: ['strong', 'sweet', 'dairy'], vanilla_latte: ['strong', 'sweet', 'dairy'], nitro_cold_brew: ['strong', 'iced'], vanilla_nitro: ['strong', 'iced', 'sweet'], nitro_float: ['strong', 'iced', 'dairy'], nitro_ice_float: ['strong', 'iced', 'dairy', 'dessert', 'sweet'], cinnamon_latte: ['strong', 'dairy', 'sweet', 'spice'], iced_cinnamon_latte: ['strong', 'dairy', 'sweet', 'spice', 'iced'], cardamom_espresso: ['strong', 'spice'], nutmeg_cappuccino: ['strong', 'dairy', 'spice'], oat_milk_latte: ['strong', 'alt_milk'], almond_cappuccino: ['strong', 'alt_milk'], iced_coconut_latte: ['strong', 'alt_milk', 'iced'], pumpkin_spice_latte: ['strong', 'dairy', 'sweet', 'seasonal', 'spice'], peppermint_mocha: ['strong', 'dairy', 'sweet', 'seasonal'], rose_latte: ['strong', 'dairy', 'sweet', 'fusion'], tahini_honey_latte: ['strong', 'dairy', 'sweet', 'fusion'], black_sesame_latte: ['strong', 'dairy', 'sweet', 'fusion'], turkish_coffee: ['strong', 'fusion'], dirty_matcha: ['strong', 'fusion'], chocolate_milk: ['sweet', 'dairy'],
    saffron: ['spice', 'fusion'], ube: ['sweet', 'fusion'],
    saffron_latte: ['strong', 'dairy', 'spice', 'fusion'], ube_latte: ['strong', 'dairy', 'sweet', 'fusion'],
  },
  ITEMS: {
    // Base Ingredients
    water: { name: "Water", type: "ingredient", description: "Pure, clean water", price: 1, consumable: true, unlockLevel: 1 },
    beans: { name: "Coffee Beans", type: "ingredient", description: "Premium coffee beans", price: 3, consumable: true, unlockLevel: 1 },
    milk: { name: "Milk", type: "ingredient", description: "Fresh dairy milk", price: 2, consumable: true, unlockLevel: 1 },
    sugar: { name: "Sugar", type: "ingredient", description: "White granulated sugar", price: 1, consumable: true, unlockLevel: 1 },
    ice: { name: "Ice", type: "ingredient", description: "Frozen water cubes", price: 1, consumable: true, unlockLevel: 1 },

    // Base Tools
    grinder: { name: "Grinder", type: "tool", description: "Grinds coffee beans", price: 0, consumable: false, unlockLevel: 1 },
    espresso_machine: { name: "Espresso Machine", type: "tool", description: "Makes espresso shots", price: 0, consumable: false, unlockLevel: 1 },
    steamer: { name: "Steamer", type: "tool", description: "Steams and heats ingredients", price: 0, consumable: false, unlockLevel: 1 },
    kettle: { name: "Kettle", type: "tool", description: "Heats water to precise temperatures", price: 0, consumable: false, unlockLevel: 1 },

    // Unlockable Items
    cocoa_powder: { name: "Cocoa Powder", type: "ingredient", description: "Rich chocolate powder", price: 4, consumable: true, unlockLevel: 2 },
    vanilla: { name: "Vanilla", type: "ingredient", description: "Pure vanilla extract", price: 5, consumable: true, unlockLevel: 3 },
    shaker: { name: "Shaker", type: "tool", description: "Whips and mixes ingredients", price: 0, consumable: false, unlockLevel: 4 },
    ice_cream: { name: "Ice Cream", type: "ingredient", description: "Creamy vanilla ice cream", price: 6, consumable: true, unlockLevel: 5 },
    nitro_tap: { name: "Nitro Tap", type: "tool", description: "Infuses drinks with nitrogen for smooth texture", price: 0, consumable: false, unlockLevel: 1 },
    brew_station_2: { name: "Second Brew Station", type: "tool", description: "An additional station to brew more drinks.", price: 0, consumable: false, unlockLevel: 5 },

    // Spices (L3)
    cinnamon: { name: "Cinnamon", type: "ingredient", description: "Aromatic spice for warmth.", consumable: true, unlockLevel: 3 },
    cardamom: { name: "Cardamom", type: "ingredient", description: "Aromatic, resinous spice.", consumable: true, unlockLevel: 3 },
    nutmeg: { name: "Nutmeg", type: "ingredient", description: "Sweet and pungent spice.", consumable: true, unlockLevel: 3 },

    // Alt Milks (L4)
    oat_milk: { name: "Oat Milk", type: "ingredient", description: "Creamy, plant-based milk.", consumable: true, unlockLevel: 4 },
    almond_milk: { name: "Almond Milk", type: "ingredient", description: "Light, nutty milk alternative.", consumable: true, unlockLevel: 4 },
    coconut_milk: { name: "Coconut Milk", type: "ingredient", description: "Rich, tropical milk.", consumable: true, unlockLevel: 4, season: 'SUMMER' },

    // International (L5)
    rose_petals: { name: "Rose Petals", type: "ingredient", description: "Fragrant, edible flowers.", consumable: true, unlockLevel: 5, season: 'SPRING' },
    tahini: { name: "Tahini", type: "ingredient", description: "Toasted sesame paste.", consumable: true, unlockLevel: 5 },
    honey: { name: "Honey", type: "ingredient", description: "Natural, sweet syrup.", consumable: true, unlockLevel: 5 },
    black_sesame_paste: { name: "Black Sesame Paste", type: "ingredient", description: "Nutty, earthy paste.", consumable: true, unlockLevel: 5 },

    // Fusion (L6)
    turkish_grinder: { name: "Turkish Grinder", type: "tool", description: "Creates an extra-fine grind.", consumable: false, unlockLevel: 6 },
    matcha: { name: "Matcha Powder", type: "ingredient", description: "Vibrant green tea powder.", consumable: true, unlockLevel: 6 },

    // Seasonal
    pumpkin_spice: { name: "Pumpkin Spice", type: "ingredient", description: "A blend of autumn spices.", consumable: true, unlockLevel: 1, season: 'FALL' },
    peppermint: { name: "Peppermint", type: "ingredient", description: "Cool, refreshing mint.", consumable: true, unlockLevel: 1, season: 'WINTER' },

    // Prestige
    saffron: { name: "Saffron", type: "ingredient", description: "The world's most precious spice.", consumable: true, unlockLevel: 1, prestige: 1 },
    ube: { name: "Ube", type: "ingredient", description: "A sweet purple yam from the Philippines.", consumable: true, unlockLevel: 1, prestige: 2 },
  },
  RECIPES: {
    // Transformations
    beans_grinder: { ingredients: ["beans", "grinder"], result: { id: "ground_coffee", name: "Ground Coffee", type: "ingredient", description: "Freshly ground coffee beans", price: 5, consumable: true }},
    ground_coffee_espresso_machine: { ingredients: ["ground_coffee", "espresso_machine"], result: { id: "espresso", name: "Espresso", type: "beverage", description: "Rich, concentrated coffee shot", price: 3, consumable: true }},
    milk_steamer: { ingredients: ["milk", "steamer"], result: { id: "steamed_milk", name: "Steamed Milk", type: "ingredient", description: "Hot, creamy steamed milk", price: 4, consumable: true }},
    steamed_milk_steamer: { ingredients: ["steamed_milk", "steamer"], result: { id: "milk_foam", name: "Milk Foam", type: "ingredient", description: "Light, airy milk foam", price: 5, consumable: true }},
    sugar_water: { ingredients: ["sugar", "water"], result: { id: "simple_syrup", name: "Simple Syrup", type: "ingredient", description: "Sweet sugar syrup", price: 3, consumable: true }},
    simple_syrup_steamer: { ingredients: ["simple_syrup", "steamer"], result: { id: "caramel_syrup", name: "Caramel Syrup", type: "ingredient", description: "Rich caramel flavoring", price: 6, consumable: true }},

    // Classics
    espresso_water: { ingredients: ["espresso", "water"], result: { id: "americano", name: "Americano", type: "beverage", description: "Espresso diluted with hot water", price: 3, consumable: true }},
    espresso_steamed_milk: { ingredients: ["espresso", "steamed_milk"], result: { id: "flat_white", name: "Flat White", type: "beverage", description: "Espresso with microfoam milk", price: 12, consumable: true }},
    steamed_milk_milk_foam: { ingredients: ["steamed_milk", "milk_foam"], result: { id: "cappuccino", name: "Cappuccino", type: "beverage", description: "Espresso topped with milk foam", price: 5, consumable: true }},
    espresso_milk: { ingredients: ["espresso", "milk"], result: { id: "latte", name: "Latte", type: "beverage", description: "Espresso with steamed milk", price: 4, consumable: true }},
    latte_milk_foam: { ingredients: ["latte", "milk_foam"], result: { id: "latte_art_latte", name: "Latte Art Latte", type: "beverage", description: "Beautiful latte with artistic foam design", price: 15, consumable: true }},
    espresso_simple_syrup: { ingredients: ["espresso", "simple_syrup"], result: { id: "sweet_espresso", name: "Sweet Espresso", type: "beverage", description: "Sweetened espresso shot", price: 9, consumable: true }},
    sweet_espresso_milk: { ingredients: ["sweet_espresso", "milk"], result: { id: "spanish_latte", name: "Spanish Latte", type: "beverage", description: "Sweet espresso with milk", price: 13, consumable: true }},

    // Iced Line
    americano_ice: { ingredients: ["americano", "ice"], result: { id: "iced_americano", name: "Iced Americano", type: "beverage", description: "Chilled americano over ice", price: 11, consumable: true }},
    latte_ice: { ingredients: ["latte", "ice"], result: { id: "iced_latte", name: "Iced Latte", type: "beverage", description: "Cold latte over ice", price: 12, consumable: true }},
    espresso_ice: { ingredients: ["espresso", "ice"], result: { id: "iced_espresso", name: "Iced Espresso", type: "beverage", description: "Chilled espresso shot", price: 9, consumable: true }},
    ground_coffee_ice: { ingredients: ["ground_coffee", "ice"], result: { id: "cold_brew", name: "Cold Brew", type: "beverage", description: "Smooth cold-brewed coffee", price: 10, consumable: true }},
    cold_brew_milk: { ingredients: ["cold_brew", "milk"], result: { id: "cold_brew_latte", name: "Cold Brew Latte", type: "beverage", description: "Cold brew with milk", price: 13, consumable: true }},
    cold_brew_simple_syrup: { ingredients: ["cold_brew", "simple_syrup"], result: { id: "sweet_cold_brew", name: "Sweet Cold Brew", type: "beverage", description: "Sweetened cold brew coffee", price: 11, consumable: true }},

    // Chocolate Branch
    cocoa_powder_simple_syrup: { ingredients: ["cocoa_powder", "simple_syrup"], result: { id: "chocolate_syrup", name: "Chocolate Syrup", type: "ingredient", description: "Rich chocolate flavoring", price: 7, consumable: true }},
    chocolate_syrup_milk: { ingredients: ["chocolate_syrup", "milk"], result: { id: "chocolate_milk", name: "Chocolate Milk", type: "beverage", description: "Sweet chocolate-flavored milk", price: 8, consumable: true }},
    chocolate_milk_espresso: { ingredients: ["chocolate_milk", "espresso"], result: { id: "mocha", name: "Mocha", type: "beverage", description: "Coffee and chocolate perfection", price: 6, consumable: true }},
    mocha_ice: { ingredients: ["mocha", "ice"], result: { id: "iced_mocha", name: "Iced Mocha", type: "beverage", description: "Chilled mocha over ice", price: 15, consumable: true }},

    // Caramel Branch
    caramel_syrup_latte: { ingredients: ["caramel_syrup", "latte"], result: { id: "caramel_latte", name: "Caramel Latte", type: "beverage", description: "Latte with caramel sweetness", price: 5, consumable: true }},
    caramel_syrup_milk_foam: { ingredients: ["caramel_syrup", "milk_foam"], result: { id: "caramel_cappuccino", name: "Caramel Cappuccino", type: "beverage", description: "Cappuccino with caramel foam", price: 14, consumable: true }},

    // Desserts
    espresso_ice_cream: { ingredients: ["espresso", "ice_cream"], result: { id: "affogato", name: "Affogato", type: "dessert-beverage", description: "Espresso poured over vanilla ice cream", price: 7, consumable: true }},
    affogato_chocolate_syrup: { ingredients: ["affogato", "chocolate_syrup"], result: { id: "sundae_affogato", name: "Sundae Affogato", type: "dessert-beverage", description: "Affogato with chocolate drizzle", price: 18, consumable: true }},

    // Shaker Line
    milk_shaker: { ingredients: ["milk", "shaker"], result: { id: "whipped_cream", name: "Whipped Cream", type: "ingredient", description: "Light, fluffy whipped cream", price: 5, consumable: true }},
    espresso_whipped_cream: { ingredients: ["espresso", "whipped_cream"], result: { id: "espresso_con_panna", name: "Espresso Con Panna", type: "beverage", description: "Espresso topped with whipped cream", price: 5, consumable: true }},

    // Vanilla Line
    simple_syrup_vanilla: { ingredients: ["simple_syrup", "vanilla"], result: { id: "vanilla_syrup", name: "Vanilla Syrup", type: "ingredient", description: "Sweet vanilla flavoring", price: 6, consumable: true }},
    vanilla_syrup_latte: { ingredients: ["vanilla_syrup", "latte"], result: { id: "vanilla_latte", name: "Vanilla Latte", type: "beverage", description: "Latte with vanilla sweetness", price: 5, consumable: true }},

    // Nitro Line (unlocked after Critic Visit)
    cold_brew_nitro_tap: { ingredients: ["cold_brew", "nitro_tap"], result: { id: "nitro_cold_brew", name: "Nitro Cold Brew", type: "beverage", description: "Smooth cold brew infused with nitrogen", price: 14, consumable: true }},
    nitro_cold_brew_vanilla_syrup: { ingredients: ["nitro_cold_brew", "vanilla_syrup"], result: { id: "vanilla_nitro", name: "Vanilla Nitro", type: "beverage", description: "Nitro cold brew with vanilla sweetness", price: 16, consumable: true }},
    nitro_cold_brew_milk: { ingredients: ["nitro_cold_brew", "milk"], result: { id: "nitro_float", name: "Nitro Float", type: "beverage", description: "Nitro cold brew with creamy milk", price: 15, consumable: true }},
    nitro_float_ice_cream: { ingredients: ["nitro_float", "ice_cream"], result: { id: "nitro_ice_float", name: "Nitro Ice Float", type: "dessert-beverage", description: "Nitro float topped with ice cream", price: 20, consumable: true }},

    // Spices (L3)
    simple_syrup_cinnamon: { ingredients: ["simple_syrup", "cinnamon"], result: { id: "cinnamon_syrup", name: "Cinnamon Syrup", type: "ingredient", description: "Sweet and spicy syrup.", price: 8, consumable: true }},
    latte_cinnamon_syrup: { ingredients: ["latte", "cinnamon_syrup"], result: { id: "cinnamon_latte", name: "Cinnamon Latte", type: "beverage", description: "A latte with a touch of warmth.", price: 14, consumable: true }},
    cinnamon_latte_ice: { ingredients: ["cinnamon_latte", "ice"], result: { id: "iced_cinnamon_latte", name: "Iced Cinnamon Latte", type: "beverage", description: "A chilled, spiced latte.", price: 15, consumable: true }},
    espresso_cardamom: { ingredients: ["espresso", "cardamom"], result: { id: "cardamom_espresso", name: "Cardamom Espresso", type: "beverage", description: "Aromatic and bold espresso.", price: 15, consumable: true }},
    cappuccino_nutmeg: { ingredients: ["cappuccino", "nutmeg"], result: { id: "nutmeg_cappuccino", name: "Nutmeg Cappuccino", type: "beverage", description: "A classic with a nutty twist.", price: 14, consumable: true }},

    // Alt Milks (L4)
    latte_oat_milk: { ingredients: ["latte", "oat_milk"], result: { id: "oat_milk_latte", name: "Oat Milk Latte", type: "beverage", description: "A creamy, dairy-free latte.", price: 13, consumable: true }},
    cappuccino_almond_milk: { ingredients: ["cappuccino", "almond_milk"], result: { id: "almond_cappuccino", name: "Almond Cappuccino", type: "beverage", description: "A nutty, frothy delight.", price: 13, consumable: true }},
    iced_latte_coconut_milk: { ingredients: ["iced_latte", "coconut_milk"], result: { id: "iced_coconut_latte", name: "Iced Coconut Latte", type: "beverage", description: "A tropical, chilled coffee.", price: 16, consumable: true }},

    // Seasonal
    simple_syrup_pumpkin_spice: { ingredients: ["simple_syrup", "pumpkin_spice"], result: { id: "pumpkin_spice_syrup", name: "Pumpkin Spice Syrup", type: "ingredient", description: "The taste of autumn.", price: 12, consumable: true }},
    latte_pumpkin_spice_syrup: { ingredients: ["latte", "pumpkin_spice_syrup"], result: { id: "pumpkin_spice_latte", name: "Pumpkin Spice Latte", type: "beverage", description: "A seasonal favorite.", price: 17, consumable: true }},
    mocha_peppermint: { ingredients: ["mocha", "peppermint"], result: { id: "peppermint_mocha", name: "Peppermint Mocha", type: "beverage", description: "A festive chocolate mint treat.", price: 18, consumable: true }},

    // International (L5)
    simple_syrup_rose_petals: { ingredients: ["simple_syrup", "rose_petals"], result: { id: "rose_syrup", name: "Rose Syrup", type: "ingredient", description: "A fragrant, floral syrup.", price: 16, consumable: true }},
    latte_rose_syrup: { ingredients: ["latte", "rose_syrup"], result: { id: "rose_latte", name: "Rose Latte", type: "beverage", description: "An elegant, aromatic latte.", price: 19, consumable: true }},
    tahini_honey: { ingredients: ["tahini", "honey"], result: { id: "tahini_honey_syrup", name: "Tahini Honey Syrup", type: "ingredient", description: "Sweet, nutty, and complex.", price: 18, consumable: true }},
    latte_tahini_honey_syrup: { ingredients: ["latte", "tahini_honey_syrup"], result: { id: "tahini_honey_latte", name: "Tahini Honey Latte", type: "beverage", description: "A unique and savory-sweet latte.", price: 22, consumable: true }},
    simple_syrup_black_sesame_paste: { ingredients: ["simple_syrup", "black_sesame_paste"], result: { id: "black_sesame_syrup", name: "Black Sesame Syrup", type: "ingredient", description: "Earthy and nutty syrup.", price: 19, consumable: true }},
    latte_black_sesame_syrup: { ingredients: ["latte", "black_sesame_syrup"], result: { id: "black_sesame_latte", name: "Black Sesame Latte", type: "beverage", description: "A striking and flavorful latte.", price: 23, consumable: true }},

    // Fusion (L6)
    beans_turkish_grinder: { ingredients: ["beans", "turkish_grinder"], result: { id: "turkish_grind", name: "Turkish Grind", type: "ingredient", description: "Coffee ground to a fine powder.", price: 8, consumable: true }},
    turkish_grind_water: { ingredients: ["turkish_grind", "water"], result: { id: "turkish_coffee", name: "Turkish Coffee", type: "beverage", description: "Strong, unfiltered coffee.", price: 20, consumable: true }},
    matcha_water: { ingredients: ["matcha", "water"], result: { id: "matcha_shot", name: "Matcha Shot", type: "ingredient", description: "A concentrated shot of green tea.", price: 20, consumable: true }},
    matcha_shot_espresso: { ingredients: ["matcha_shot", "espresso"], result: { id: "dirty_matcha", name: "Dirty Matcha", type: "beverage", description: "Earthy matcha meets bold espresso.", price: 25, consumable: true }},

    // Prestige
    latte_saffron: { ingredients: ["latte", "saffron"], result: { id: "saffron_latte", name: "Saffron Latte", type: "beverage", description: "An exotic, golden latte.", price: 28, consumable: true }},
    latte_ube: { ingredients: ["latte", "ube"], result: { id: "ube_latte", name: "Ube Latte", type: "beverage", description: "A vibrant and sweet purple latte.", price: 26, consumable: true }},
  },

  // --- SPLASH SCREEN CONSTANTS ---
  SPLASH: {
    text: 'made with',
    backgroundColor: '#F7F7F7',
    textColor: '#2D2D2D',
    imageCategory: 'logos',
    imageKey: 'splash',
    imageDisplayWidth: 192,
    imageDisplayHeight: 192,
    popScaleFrom: 0.8,
    popOvershoot: 1.08,
    popScaleTo: 1.0,
    popDurationMs: 700,
    gridColor: '#CCCCCC',
    gridDotSize: 1,
    gridSpacing: 20,
    fadeOutDelayMs: 3000,
    fadeOutDurationMs: 800,
    fadeOutScaleTo: 0.8
  }
};
